/* eslint-disable @typescript-eslint/no-explicit-any */
import { signInAnonymously, type User } from 'firebase/auth';
import { auth, db } from '../firebase';
import type { BootcampDelivery, NucampUser } from '$lib/datamodel';
import dayjs from 'dayjs';
import { DocumentReference, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { CaptureUserData } from './nurture';
import { addUserToBootcampFollowList } from './bootcamp';
import { analyticsEvent, captureUserEmail, setUserData } from './analytics';
import { get } from 'svelte/store';
import { user } from '../stores/nucamp-user';
import { getUTN, mergeUTNCodes } from './window';
import { identifyUser } from './crisp';
import { geolocation } from "$lib/client/stores/nucamp-geolocation";

export async function followBootcamp(userData: NucampUser, bootcamp: BootcampDelivery) {
  console.time('getUser');
  const loggedUser = get(user);
  console.timeEnd('getUser');
  if (!loggedUser?.uid) {
    console.time('followBootcampAnonymous');
    await followBootcampAnonymous(userData, bootcamp);
    console.timeEnd('followBootcampAnonymous');
  } else if (!(bootcamp && bootcamp.followers[loggedUser.uid] === true)) {
    console.time('addUserToBootcampFollowList 1');
    await addUserToBootcampFollowList(loggedUser as NucampUser, bootcamp)
    console.timeEnd('addUserToBootcampFollowList 1');
  }
}

export async function followBootcampAnonymous(userData: NucampUser, bootcamp: BootcampDelivery) {
  console.time('signInAnonymously');
  const credentials = await signInAnonymously(auth);
  console.timeEnd('signInAnonymously');
  
  if (credentials.user && credentials.user.uid && credentials.user.uid !== 'undefined') {
    console.time('createNucampAnonymousUser');
    const userUpdated = await createNucampAnonymousUser(credentials.user, userData, bootcamp);
    console.timeEnd('createNucampAnonymousUser');
    if (!(bootcamp && bootcamp.followers[userUpdated.uid] === true)) {
      console.time('addUserToBootcampFollowList 2');
      await addUserToBootcampFollowList(userUpdated as NucampUser, bootcamp)
      console.timeEnd('addUserToBootcampFollowList 2');
    }
  }
}

async function createNucampAnonymousUser(user: User, userData: Partial<NucampUser>, bootcamp: BootcampDelivery) {
  const location = get(geolocation);

  const newFollower:any = {
    phone: userData?.phone ? userData?.phone.replace(/[^+\d]+/g, "") : "",
    firstname: userData?.firstname ?? "",
    lastname: userData?.lastname ?? "",
    email: userData?.email ?? "",
    authEmail: userData?.email ?? "",
    displayName: userData?.displayName ?? "",
    country: bootcamp?.community?.community?.country ?? "",
    state: bootcamp?.community?.community?.state ?? ""
  }
  if (!newFollower.country && location?.country_code2) newFollower.country = location.country_code2;

  const nucampFlex = bootcamp?.nucampFlex ? true : false;

  newFollower.photoURL = generateAvatar(newFollower.firstname, newFollower.lastname);
  const timestamp = dayjs().toDate();
  const userdata: User = {
    ...newFollower,
    uid: user.uid,
    following: {},
    auth: "anonymous",
    referral: generateReferral(),
    createdAt: timestamp,
    updatedAt: timestamp,
  }

  await updateUserData(userdata, true, {category: "Contact", action:"Capture Follower Form", label: bootcamp?.id ?? ""}, nucampFlex)
      .catch((error) => {
        console.error("Error creating anonymous user", error);
      })

  return userdata;
}

function generateAvatar(firstname: string, lastname: string) {
  const defaultcolors = ["082c6a", "7b2cc2", "cb2308", "58b908", "082c6a", "8db04f"];
  const displayName = firstname + lastname;

  let background = defaultcolors[Math.floor(Math.random() * defaultcolors.length)];
  if (displayName) {
    const displayNameBackground = hexColorFromString(displayName);
    if (displayNameBackground && displayNameBackground !== "") background = displayNameBackground;
  }

  return `https://ui-avatars.com/api/?name=${firstname.slice(0,1)}+${lastname.slice(0,1)}&background=${background}&color=fff&size=128`;
}

function componentToHex(c: number) {
  const hex = c.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}

function hexColorFromString(text: string) {
  const regbstring = text.substring(Math.floor((text.length/2)-2), Math.floor((text.length/2)+1));
  return regbstring.toLowerCase().split('')
        .filter( c => (c >= 'a' && c <= 'z') )
        .map( c => {
          return Math.floor(((c.charCodeAt(0) - 'a'.charCodeAt(0) + 1)*230)/26);
        })
        .map( c => {
          return componentToHex(c);
        })
        .join("")
}

function generateReferral() {
  const referral = {
    code: new Date().getTime().toString(36).replace(/0,o,O/g, randomLetter(1)).slice(-6).toUpperCase(),
    numberofvisits: 0,
    totalgain: 0
  }
  return referral
}

function randomLetter(numberLetters: number) {
  let text = "";
  const possible = "ABCDEFGHIJKLMNPQRSTUVWXYZ123456789";

  for (let i = 0; i < numberLetters; i++)
    text += possible.charAt(Math.floor(Math.random() * possible.length));

  return text;
}

async function updateUserData(user: any, preventTriggerMautic: boolean, event: {category: string, action: string, label: string}, nucampFlex: boolean) {
  // Sets user data to firestore on login
  const userRef: DocumentReference = doc(db, `users/${user.uid}`);
  console.time('updatedUser');
  const updatedUser = await getDoc(userRef)
    .then(async userR => {
      const existingUser = userR.data() as NucampUser;
      const dbPromise = [] as Promise<any>[];
      if (existingUser && existingUser.auth) {
        /// Do not overwrite displayName firstname, lastname and email
        if (existingUser.createdAt && existingUser.createdAt != null) user.createdAt = existingUser.createdAt;
        if (existingUser.referral) user.referral = existingUser.referral;
        if (existingUser.location) user.location = existingUser.location;
        if (existingUser.state) user.state = existingUser.state;
        if (existingUser.displayName) user.displayName = existingUser.displayName;
        if (existingUser.firstname) user.firstname = existingUser.firstname;
        if (existingUser.lastname) user.lastname = existingUser.lastname;
        if (existingUser.email) user.email = existingUser.email.trim().toLowerCase();
        if (existingUser.mauticId && existingUser.mauticId !== -1) user.mauticId = existingUser.mauticId;
        if (existingUser.discordId) user.discordId = existingUser.discordId;
      }
      else {
        const userPromise = [];
        if (!preventTriggerMautic) {
          userPromise.push(CaptureUserData(user, user.location, user.state, nucampFlex)
            .catch(err => {
              console.error("Error capturing nurturing user data", err);
              return Promise.resolve();
            }))
        }
        user.updatedAt = dayjs().toDate();
        userPromise.push(setDoc(userRef, user, { merge: true })
          .catch(err => {
            console.error("Error creating user account in firebase", err);
            return Promise.resolve();
          }));
        console.time('userPromise');
        await Promise.all(userPromise).catch();
        console.timeEnd('userPromise');
        // First time user is being identified - fire the lead tag
        dbPromise.push(captureUserEmail(user, "consumer")
          .catch(err => {
            console.error("Error capturing analytics user email", err);
            return Promise.resolve();
          })
        );
        if (event) dbPromise.push(analyticsEvent(event.category, event.action, event.label)
          .catch(err => {
            console.error("Error capturing analytics user event", err);
            return Promise.resolve();
          }));
      }
      console.time('dbPromise');
      await Promise.all(dbPromise).catch((err) => {
        console.error("Error updating user", err);
        return Promise.resolve();
      });
      console.timeEnd('dbPromise');
      return user;
    })
    .catch(err => {
      console.error("Error updating user", err);
      return user;
    })
  console.timeEnd('updatedUser');
  return updatedUser;
}

export function syncUser (user: NucampUser) {
  if (user) {
    // add firstVisit info if there isn't one
    const fv = window.localStorage.getItem('firstVisit');
    let fvDirty = false;
    if (!user.firstVisit) {
      //check if the account was created before the firstVisit... if that's the case then do not write firstVisit (it's wrong)
      if (fv && user.createdAt && dayjs(fv).isBefore(dayjs(user.createdAt))) {
        user.firstVisit = dayjs(fv).toDate();
        fvDirty = true;
      }
    } else if (user.firstVisit) {
      // Update firstVisit info if there is a new one, but only if it identifies a newer firstVisit
      if (fv) {
        if(dayjs(user.firstVisit).isAfter(dayjs(fv))) {
          user.firstVisit = dayjs(fv).toDate();
          fvDirty = true;
        }
      }
    }
    if (!user.email || (user.email && !user.email.includes('nucamp.co'))) {
      syncTraffic(user);
    }
    if (fvDirty) {
      updateDoc(doc(db, `users/${user.uid}`), {firstVisit: user.firstVisit})
    }
    setUserData(user);
    identifyUser(user);
  } else {
    identifyUser(undefined);
  }
}


export function isRoles(user: NucampUser, roles: string[]) {
  let isRole = false;
  if (user?.roles) {
    isRole = roles.some(role => user.roles && user.roles[role]);
  }
  return isRole;
}

export function syncTraffic(user?: NucampUser) {
  if (user && user.uid) {
    const usersextRef: DocumentReference = doc(db, `usersext/${user.uid}`)

    getDoc(usersextRef).then(doc => {
      let dirty = false;
      const now = dayjs().toDate();
      const userext = doc.data();
      const data: any = {uid: user.uid}

      if (!doc.exists()) {
        data.createdAt = now;
      }
      // Add tracking from local storage if exists
      if (!doc.exists() || (doc.exists() && !userext?.tracking)) {
        const utn = getUTN();
        if (utn) {
          data.tracking = utn;
          if (data.tracking && Array.isArray(data.tracking)) {
            const referrals = data.tracking
              .filter((track: any) => track.campaign === "referral")
              .reduce((acc: any, cur: any) => {
                if (!acc.includes(cur.source)) acc.push(cur.source);
                return acc;
              }, []);
            if (referrals && referrals.length > 0) {
              data.referrals = referrals;
            }
          }
          dirty = true;
        }
      }

      // Merge tracking from local storage / URL if exists
      if (doc.exists() && userext?.tracking) {
        const merged = mergeUTNCodes(userext.tracking);
        data.tracking = merged.merged;
        if (data.tracking && Array.isArray(data.tracking)) {
          const referrals = data.tracking
            .filter((track: any) => track.campaign === "referral")
            .reduce((acc:any, cur:any) => {
              if (!acc.includes(cur.source)) acc.push(cur.source);
              return acc;
            }, []);
          if (referrals && referrals.length > 0) {
            data.referrals = referrals;
          }
        }
        dirty = merged.dirty;
      }

      if (dirty) {
        data.updatedAt = now;
        setDoc(usersextRef, data, {merge: true})
          .catch((err: any) => console.error("Error with traffic.syncTraffic", err));
      }
    })
  }
}